/**
 * Finger painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const fingerPaintingIdeasPack: ToilePack = {
    slug: 'finger-painting-ideas',
    category: 'craft',
    name: 'Finger painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'tree', label: 'Blossom tree', subject: 'a tree with a brown painted trunk and a crown of pink and white fingerprint blossoms'},
        {key: 'fish', label: 'Fingerprint fish', subject: 'a blue sea full of colourful fingerprint fish, each with a drawn eye and tail'},
        {key: 'caterpillar', label: 'Caterpillar', subject: 'a caterpillar of green thumbprints climbing a big painted leaf'},
    ],
    styles: [
        {key: 'table', label: 'On the table', style: 'drying flat on a kitchen table beside open pots of paint and a damp cloth', palette: 'Light wood table and primary-coloured paint pots around the white sheet.', framing: 'Seen from directly above, the sheet centred with the pots at the margins.'},
        {key: 'fridge', label: 'On the fridge', style: 'stuck to a white fridge door with two round fruit magnets', palette: 'White fridge and two bright magnets, the painting the main colour.', framing: 'Seen straight on at eye level, the sheet centred on the door.'},
        {key: 'line', label: 'Drying line', style: 'pegged with wooden clothes pegs to a string drying line across a pale playroom wall', palette: 'Pale wall and natural pegs, the paint colours bright against them.', framing: 'Seen straight on at eye level, the sheet centred and hanging slightly curved.'},
    ],
    directions: ['rainbow', 'sunflowers', 'ladybugs'],
    directionHint: 'try: rainbow',
    seo: {
        title: 'Finger Painting Ideas: Blossom Tree, Fish, Caterpillar',
        metaDescription: 'Nine finger painting ideas for small children: a fingerprint blossom tree, a sea of fish and a thumbprint caterpillar, on the table, fridge and line.',
        h1: 'Finger painting ideas: blossom tree, fingerprint fish, thumbprint caterpillar',
        tagline: 'Nine finished finger paintings, three designs a small child can make, each shown drying on a table, on a fridge and on a line.',
        intro: 'Parents and preschool teachers look for finger painting a small child can finish and be proud of. These nine show three designs made from fingerprints and thumbprints: a tree with a brown trunk and a crown of pink and white blossoms, a blue sea full of fish with drawn eyes, and a green caterpillar climbing a big leaf. Type another idea, such as a rainbow or ladybugs, and the same method carries it.',
        useCases: [
            'a preschool wall display',
            'a family fridge gallery',
            'a rainy-day activity',
            'a card for grandparents',
        ],
        faq: [
            {q: 'Which age is this for?', a: 'The pictures are styled as the work of a three-year-old. The method uses only fingers and washable paint, so it suits small children.'},
            {q: 'What paint and paper are shown?', a: 'Bright washable poster paint on thick white paper, the kind that dries flat on a table.'},
            {q: 'Can I change the design?', a: 'Yes. Type another design and the same fingerprint method and the three settings carry it.'},
            {q: 'Where is each painting shown?', a: 'Drying flat on a kitchen table, stuck to a white fridge door with fruit magnets, or pegged to a string line across a playroom wall.'},
        ],
    },
}
